import ReactRailsUJS from 'react_ujs';

/**
 * @description Registers a require.context of mountable React components with react_ujs.
 *
 * Use this instead of calling ReactRailsUJS.useContext directly. useContext replaces
 * ReactRailsUJS.getConstructor outright, so when more than one pack calls it on a page
 * the last one to run wins and the other pack's components stop resolving.
 *
 * That is harmless under webpack 4 without splitChunks, because each pack bundles its
 * own private copy of react_ujs and therefore its own getConstructor. Since the move to
 * shakapacker/webpack 5, splitChunks hoists react_ujs into a chunk shared by every pack,
 * so all packs mutate one ReactRailsUJS instance. Registering contexts here keeps them
 * additive: lookups try each registered context in turn.
 *
 * Component names stay relative to their own context, so `react_component 'Inspection/index'`
 * resolves against components/ and `react_component 'Requests/index'` against salute_portal/
 * exactly as before.
 */
const contexts = [];

// Quacks like a require.context: react_ujs only ever calls it to resolve a path, so
// delegating to each registered context is enough. Throws the last error when no
// context can resolve the path, which is what react_ujs expects for its global fallback.
// webpack's context modules throw a MODULE_NOT_FOUND error for a path they don't hold.
const isModuleNotFound = error =>
  !!error && (error.code === 'MODULE_NOT_FOUND' || /Cannot find module/.test(error.message || ''));

const combinedContext = path => {
  let notFound;

  for (let i = 0; i < contexts.length; i += 1) {
    try {
      return contexts[i](path);
    } catch (error) {
      // Only a genuine "this context doesn't hold that path" miss may fall through to the
      // next context. Anything else -- most importantly the component module itself
      // throwing while being evaluated -- has to propagate, because react_ujs wraps this
      // call in a catch-all that would otherwise turn a real error into a misleading
      // "Cannot find component".
      if (!isModuleNotFound(error)) throw error;
      notFound = error;
    }
  }

  throw notFound || new Error(`No component context can resolve ${path}`);
};

const registerComponentContext = requireContext => {
  if (!contexts.includes(requireContext)) contexts.push(requireContext);

  ReactRailsUJS.useContext(combinedContext);
};

export default registerComponentContext;
